<template>
    <section id="planes" class="sp-section sp-section--alt sp-planes-section">
        <!-- Orbe de luz ambiental -->
        <div class="sp-glow-orb sp-glow-orb--cyan" style="top: 10%; right: -5%; width: 450px; height: 450px;"></div>

        <div class="sp-container">
            <div class="sp-section-head sp-reveal">
                <span class="sp-eyebrow">Planes y Tarifas</span>
                <h2 class="sp-h2">Elige la visibilidad y alcance que necesitas</h2>
                <p class="sp-lead">
                    Paga de forma flexible por días de campaña y activa tu presencia con la prioridad
                    que tu establecimiento merece.
                </p>
            </div>

            <!-- Cargando: Esqueleto elegante con shimmer -->
            <ul v-if="cargando" class="sp-planes__grid" aria-hidden="true">
                <li v-for="n in 3" :key="n">
                    <div class="sp-card sp-plan sp-plan--esqueleto">
                        <span class="sp-shimmer sp-shimmer--titulo"></span>
                        <span class="sp-shimmer sp-shimmer--texto"></span>
                        <span class="sp-shimmer sp-shimmer--texto sp-shimmer--corto"></span>
                        <span class="sp-shimmer sp-shimmer--precio"></span>
                        <span class="sp-shimmer sp-shimmer--boton"></span>
                    </div>
                </li>
            </ul>
            <p v-if="cargando" class="sp-visually-hidden" role="status">Cargando planes de publicidad…</p>

            <!-- Si vienen planes desde el backend -->
            <ul v-else-if="planes.length" class="sp-planes__grid">
                <li v-for="(plan, i) in planes" :key="plan.codigo || i" class="sp-reveal" :class="'sp-delay-' + (i + 1)">
                    <article class="sp-card sp-plan" :class="{ 'sp-plan--destacado': esDestacado(i) }">
                        <span v-if="esDestacado(i)" class="sp-plan__cinta">
                            <i class="pi pi-star-fill"></i> Más elegido
                        </span>

                        <div class="sp-plan__header">
                            <h3 class="sp-plan__nombre">{{ plan.nombre }}</h3>
                            <p class="sp-plan__desc">{{ plan.descripcion }}</p>
                        </div>

                        <div class="sp-plan__precio-caja">
                            <span class="sp-plan__desde">Tarifa por sede</span>
                            <div class="sp-plan__valor">
                                <strong>{{ precioDia(plan.precio_dia) }}</strong>
                                <span class="sp-plan__periodo">/ día</span>
                            </div>
                        </div>

                        <ul class="sp-plan__features">
                            <li><i class="pi pi-check"></i> Anuncio en mapa de ruta</li>
                            <li><i class="pi pi-check"></i> Notificación por cercanía</li>
                            <li><i class="pi pi-check"></i> Panel de estadísticas en vivo</li>
                            <li><i class="pi pi-check"></i> Soporte técnico para tu sede</li>
                        </ul>

                        <button
                            type="button"
                            class="sp-btn sp-btn--block"
                            :class="esDestacado(i) ? 'sp-btn--primary' : 'sp-btn--outline'"
                            @click="$emit('elegir-plan', plan.codigo)"
                        >
                            <span>Elegir este plan</span>
                            <i class="pi pi-arrow-right"></i>
                        </button>
                    </article>
                </li>
            </ul>

            <!-- Si el backend no responde o no tiene planes activos.
                 A propósito NO se muestra ningún precio de respaldo: las tarifas
                 son administrables desde la BD y no pueden vivir en el front (CA-08).
                 El CTA sigue visible para que la sección nunca quede muerta. -->
            <div v-else class="sp-planes__vacio sp-reveal">
                <Message severity="info" :closable="false">
                    Estamos actualizando nuestros planes de publicidad. Escríbenos y te
                    compartimos las tarifas vigentes para tu negocio.
                </Message>
                <button type="button" class="sp-btn sp-btn--primary" @click="$emit('anunciarme')">
                    <span>Quiero anunciarme</span>
                    <i class="pi pi-arrow-right" aria-hidden="true"></i>
                </button>
            </div>

            <!-- SIMULADOR INTERACTIVO DE ALCANCE Y RENDIMIENTO -->
            <div class="sp-simulador sp-reveal sp-delay-3">
                <div class="sp-simulador__header">
                    <div class="sp-simulador__tag">
                        <i class="pi pi-sliders-h"></i> Simulador interactivo
                    </div>
                    <h3 class="sp-h3">Calcula el alcance estimado de tu campaña</h3>
                    <p>Mueve la duración para proyectar el impacto en conductores cercanos a tu sede.</p>
                </div>

                <div class="sp-simulador__body">
                    <div class="sp-simulador__control">
                        <div class="sp-simulador__slider-head">
                            <span>Duración de la campaña:</span>
                            <strong>{{ diasSimulacion }} {{ diasSimulacion === 1 ? 'Día' : 'Días' }}</strong>
                        </div>
                        <input
                            v-model.number="diasSimulacion"
                            type="range"
                            min="3"
                            max="30"
                            step="1"
                            class="sp-slider"
                            aria-label="Días de campaña"
                        />
                        <div class="sp-simulador__slider-ticks">
                            <span>3 días</span>
                            <span>7 días (1 semana)</span>
                            <span>15 días</span>
                            <span>30 días (1 mes)</span>
                        </div>
                    </div>

                    <div class="sp-simulador__resultados">
                        <div class="sp-simulador__metrica">
                            <i class="pi pi-eye"></i>
                            <div>
                                <span class="sp-simulador__label">Impactos estimados</span>
                                <strong>~{{ formatearNumero(diasSimulacion * 1450) }} conductores</strong>
                            </div>
                        </div>

                        <div class="sp-simulador__metrica">
                            <i class="pi pi-map-marker"></i>
                            <div>
                                <span class="sp-simulador__label">Cobertura en ruta</span>
                                <strong>100% geocerca activa</strong>
                            </div>
                        </div>

                        <button type="button" class="sp-btn sp-btn--primary sp-simulador__btn" @click="$emit('anunciarme')">
                            <span>Cotizar esta campaña</span>
                            <i class="pi pi-arrow-right"></i>
                        </button>
                    </div>
                </div>
            </div>

            <p class="sp-planes__nota">
                <i class="pi pi-info-circle"></i> Los valores de campaña se configuran por sede activa. La activación y pago se gestionan de forma segura desde el portal de anunciantes.
            </p>
        </div>
    </section>
</template>

<script>
import axios from 'axios';

export default {
    name: 'NegociosPlanes',
    emits: ['anunciarme', 'elegir-plan'],
    data() {
        return {
            planes: [],
            cargando: true,
            diasSimulacion: 7
        };
    },
    mounted() {
        this.cargarPlanes();
    },
    methods: {
        cargarPlanes() {
            this.cargando = true;
            axios
                .get('planes-publicidad')
                .then((response) => {
                    const datos = response && response.data ? response.data.data : null;
                    this.planes = Array.isArray(datos) && datos.length ? datos : [];
                })
                .catch(() => {
                    this.planes = [];
                })
                .finally(() => {
                    this.cargando = false;
                });
        },
        precioDia(valor) {
            const numero = Number(valor);
            if (!isFinite(numero)) return '';
            return '$' + new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(numero);
        },
        formatearNumero(val) {
            return new Intl.NumberFormat('es-CO').format(val);
        },
        esDestacado(i) {
            return this.planes.length === 3 && i === 1;
        }
    }
};
</script>

<style lang="scss" scoped>
.sp-planes-section {
    position: relative;
    overflow: hidden;
}

.sp-planes__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.75rem;
    align-items: stretch;
    padding-top: 1rem;
}

.sp-plan {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: clamp(1.8rem, 3vw, 2.5rem);
    background: #ffffff;
    border-radius: var(--sp-radius-lg);
    border: 1px solid var(--sp-border);
    transition: transform var(--sp-transition-normal), box-shadow var(--sp-transition-normal), border-color var(--sp-transition-normal);

    &:hover {
        transform: translateY(-8px);
        border-color: rgba(235, 93, 27, 0.45);
        box-shadow: 0 20px 45px rgba(13, 48, 68, 0.12);
    }
}

.sp-plan--destacado {
    border-color: var(--sp-orange);
    box-shadow: 0 16px 40px rgba(235, 93, 27, 0.22);
    background: linear-gradient(180deg, #ffffff 0%, #fffdfb 100%);

    &:hover {
        box-shadow: 0 24px 55px rgba(235, 93, 27, 0.32);
    }
}

.sp-plan__cinta {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 1.25rem;
    background: linear-gradient(135deg, var(--sp-orange) 0%, #ff7536 100%);
    border-radius: 999px;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    box-shadow: 0 4px 14px rgba(235, 93, 27, 0.45);
}

.sp-plan__header {
    text-align: center;
    padding-bottom: 1.25rem;
    border-bottom: 1px solid var(--sp-border);
}

.sp-plan__nombre {
    font-size: clamp(1.2rem, 1.8vw, 1.45rem);
    font-weight: 800;
}

.sp-plan__desc {
    margin-top: 0.4rem;
    color: var(--sp-text-soft);
    font-size: 0.92rem;
    line-height: 1.5;
}

.sp-plan__precio-caja {
    text-align: center;
    padding: 1.4rem 0 1rem;
}

.sp-plan__desde {
    display: block;
    color: var(--sp-text-soft);
    font-size: 0.84rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.sp-plan__valor {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.35rem;
    margin-top: 0.2rem;
    color: var(--sp-navy);

    strong {
        font-size: clamp(2rem, 3.8vw, 2.6rem);
        font-weight: 800;
        letter-spacing: -0.02em;
        line-height: 1.1;
    }
}

.sp-plan__periodo {
    color: var(--sp-text-soft);
    font-size: 0.95rem;
    font-weight: 600;
}

.sp-plan__features {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin: 1.25rem 0 2rem;
    padding: 0;

    li {
        display: flex;
        align-items: flex-start;
        gap: 0.65rem;
        color: var(--sp-navy);
        font-size: 0.92rem;
        font-weight: 500;

        i {
            flex: 0 0 auto;
            margin-top: 0.2rem;
            color: #22c55e;
            font-size: 0.9rem;
            font-weight: 800;
        }
    }
}

.sp-plan .sp-btn {
    margin-top: auto;
}

/* SIMULADOR */
.sp-simulador {
    margin-top: clamp(3rem, 6vw, 4.5rem);
    padding: clamp(1.75rem, 3.5vw, 2.75rem);
    background: linear-gradient(135deg, #0d3044 0%, #081f2d 100%);
    border-radius: var(--sp-radius-lg);
    box-shadow: 0 20px 50px rgba(13, 48, 68, 0.25);
    color: #fff;
}

.sp-simulador__header {
    text-align: center;
    max-width: 650px;
    margin: 0 auto clamp(1.75rem, 3vw, 2.5rem);

    h3 {
        color: #fff;
        margin-top: 0.5rem;
    }

    p {
        color: var(--sp-text-on-dark-soft);
        font-size: 0.96rem;
        margin-top: 0.35rem;
    }
}

.sp-simulador__tag {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.3rem 0.85rem;
    background: rgba(235, 93, 27, 0.2);
    border: 1px solid rgba(235, 93, 27, 0.4);
    border-radius: 999px;
    color: #ff9560;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.sp-simulador__body {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
    align-items: center;
}

.sp-simulador__control {
    background: rgba(255, 255, 255, 0.06);
    padding: 1.5rem;
    border-radius: var(--sp-radius);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.sp-simulador__slider-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    font-size: 1.05rem;

    strong {
        color: var(--sp-orange);
        font-size: 1.3rem;
    }
}

.sp-slider {
    width: 100%;
    height: 8px;
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.2);
    outline: none;
    cursor: pointer;
    accent-color: var(--sp-orange);
}

.sp-simulador__slider-ticks {
    display: flex;
    justify-content: space-between;
    margin-top: 0.65rem;
    color: rgba(255, 255, 255, 0.55);
    font-size: 0.76rem;
}

.sp-simulador__resultados {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
}

.sp-simulador__metrica {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: rgba(255, 255, 255, 0.08);
    border-radius: var(--sp-radius-sm);
    border: 1px solid rgba(255, 255, 255, 0.12);

    i {
        font-size: 1.5rem;
        color: var(--sp-orange);
    }

    strong {
        display: block;
        font-size: 1.2rem;
        color: #fff;
        font-weight: 800;
    }
}

.sp-simulador__label {
    display: block;
    font-size: 0.78rem;
    color: var(--sp-text-on-dark-soft);
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.sp-simulador__btn {
    margin-top: 0.5rem;
}

.sp-planes__nota {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 2rem;
    color: var(--sp-text-soft);
    font-size: 0.85rem;
    text-align: center;

    i {
        color: var(--sp-orange);
    }
}

/* Estado sin planes: el backend no respondió o no hay ninguno activo.
   Nunca muestra cifras; solo el aviso y el CTA. */
.sp-planes__vacio {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.25rem;
    text-align: center;

    :deep(.p-message) {
        width: 100%;
        max-width: 60ch;
        margin: 0;
    }
}

/* Shimmer de carga */
.sp-plan--esqueleto {
    gap: 0.85rem;
}

.sp-shimmer {
    display: block;
    height: 0.85rem;
    border-radius: 6px;
    background: linear-gradient(90deg, #e9eef1 25%, #f4f7f9 37%, #e9eef1 63%);
    background-size: 400% 100%;
    animation: sp-shimmer 1.4s ease infinite;
}

.sp-shimmer--titulo {
    width: 50%;
    height: 1.4rem;
    margin: 0 auto;
}

.sp-shimmer--texto {
    width: 90%;
    margin: 0 auto;
}

.sp-shimmer--corto {
    width: 60%;
}

.sp-shimmer--precio {
    width: 60%;
    height: 2.6rem;
    margin: 1.25rem auto 0;
}

.sp-shimmer--boton {
    width: 100%;
    height: 3rem;
    border-radius: 999px;
    margin-top: 1rem;
}

@keyframes sp-shimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
}

@media (min-width: 768px) {
    .sp-planes__grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .sp-simulador__body {
        grid-template-columns: 1.15fr 0.85fr;
    }
}

@media (min-width: 992px) {
    .sp-planes__grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 2rem;
    }
}

/* --------------------------------------------------------------------------
   Pantallas anchas pero con poca altura (el caso tipico de un 27": 2560 de
   ancho pero ~1300px de viewport util). Sin esto la seccion mide 1453px y el
   simulador quedaba cortado por la mitad al llegar desde el menu, que se lee
   como un panel roto. Aqui se recupera altura del espaciado, sin tocar el
   tamano del contenido.
   -------------------------------------------------------------------------- */
@media (min-width: 1600px) and (max-height: 1440px) {
    .sp-planes-section {
        padding-top: 4rem;
        padding-bottom: 4rem;
    }

    .sp-planes-section .sp-section-head {
        margin-bottom: 2rem;
    }

    .sp-planes__grid {
        padding-top: 0.5rem;
    }

    .sp-simulador {
        margin-top: 2.25rem;
        padding: 1.75rem;
    }

    .sp-simulador__header {
        margin-bottom: 1.25rem;
    }

    .sp-planes__nota {
        margin-top: 1.25rem;
    }
}
</style>
